import { useCallback, useState, MouseEvent } from 'react';
import { IChartLine } from '../useChartLines';
import { debounce, isNil } from 'lodash';
import {
  FunnelModeKey,
  TimeUnit,
} from '../../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../../utils/types';
import { DATA_KEY } from '../../constants/utils';
import {
  FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY,
  IDisplayedHighlight,
} from '../../interfaces';
import { FUNNEL_FREQUENCY_X_AXIS_KEY } from '../../interfaces';
import useTooltipScrollCancellation from './useTooltipScrollCancellation';
import { useChartContext } from '../../ChartContext';
import { IAnalysisResponse } from '../../../../utils/apollo/responseTypes';

const TOOLTIP_POSITION_OFFSET = 20;

/**
 * from rechart mouse event props
 */
export interface IRechartProps {
  /**
   * x and y are the coordinates of the mouse on the chart
   */
  x: number;
  y: number;
  date: string;
  xAxisKey: string;
  frequency?: string;
  timeToConvert?: string;
  name?: string;
  barWidth?: number;
  value?: number;
  // use for ttc chart to indicate the special x label index, for example: 0-30m, 30-60m.
  // and then use this index to get correct interval from analysis.data.funnelXAxis.
  // in redux state, it's stored in chartData.xAxis.
  xAxisLabelIndex?: number;
}

/**
 * Props for the tooltip display, and prepare cohort
 */
export type ITooltipProps = {
  line: IChartLine;
  timeUnit: TimeUnit;
  startDate: string;
  endDate: string;
  highlight?: IDisplayedHighlight;
} & IRechartProps;

interface IProps {
  containerRef: React.RefObject<HTMLDivElement>;
}

const getXAxisKeyByBuilderInfo = (query: IAnalysisResponse['query']) => {
  if (query.__typename === 'FunnelQuery') {
    if (query.mode?.key === FunnelModeKey.Frequency) {
      return FUNNEL_FREQUENCY_X_AXIS_KEY;
    } else if (query.mode?.key === FunnelModeKey.TimeToConvert) {
      return FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY;
    }
  }
  return DATA_KEY;
};
/*
  This hook is used to handle the tooltip for all charts which needs to display the tooltip
  on hovering over the bar or dot. It will prepare the tooltip props and handle the tooltip position
  for display and interaction purposes.
*/
const useSingleChartDataTooltip = ({ containerRef }: IProps) => {
  const {
    metadata: { query, startDate, endDate, timeUnit },
  } = useChartContext();

  assertCondition(
    query.__typename === 'ExpressionQuery' ||
      query.__typename === 'FunnelQuery' ||
      query.__typename === 'RetentionQuery',
  );
  const [tooltipProps, setTooltipProps] = useState<ITooltipProps | null>(null);
  const [isTooltipLocked, setIsTooltipLocked] = useState(false);
  const debouncedSetTooltipProps = debounce(setTooltipProps, 100);

  const onBarMouseMove = useCallback(
    ({
      rechartProps,
      line,
      mouseEvent,
      xAxisLabelIndex,
    }: {
      rechartProps: IRechartProps;
      line: IChartLine;
      mouseEvent: MouseEvent<SVGPathElement>;
      xAxisLabelIndex?: number;
    }) => {
      if (isTooltipLocked) {
        return;
      }
      if (!rechartProps) {
        setTooltipProps(null);
      }

      // we don't display any null value tooltip
      if (isNil(rechartProps.value)) {
        return;
      }
      // Clear any pending leave timeout since mouse moved
      debouncedSetTooltipProps.cancel();

      const container = containerRef.current;
      if (!container) {
        return;
      }
      let dateLabel: string | undefined;
      if (
        getXAxisKeyByBuilderInfo(query) &&
        rechartProps[getXAxisKeyByBuilderInfo(query)]
      ) {
        dateLabel = rechartProps[getXAxisKeyByBuilderInfo(query)];
      }

      // Get the SVG bar element and its bounding rect to fixed tooltip at its top border
      if (!(mouseEvent.target instanceof SVGElement)) {
        return;
      }
      const barRect = mouseEvent.target.getBoundingClientRect();

      // Set the fixed position of the tooltip next to the top border of the bar
      const tooltipX = barRect.x + barRect.width + TOOLTIP_POSITION_OFFSET;
      const tooltipY = barRect.top;

      setTooltipProps({
        line,
        xAxisKey: rechartProps.xAxisKey,
        x: tooltipX,
        y: tooltipY,
        date: dateLabel ?? rechartProps.xAxisKey,
        timeUnit,
        startDate,
        endDate,
        barWidth: barRect.width,
        xAxisLabelIndex,
      });
    },
    [
      isTooltipLocked,
      debouncedSetTooltipProps,
      query,
      containerRef,
      endDate,
      startDate,
      timeUnit,
    ],
  );

  const onLineDotMouseEnter = useCallback(
    (
      rechartProps: IRechartProps,
      line: IChartLine,
      highlight?: IDisplayedHighlight,
    ) => {
      if (isTooltipLocked) {
        return;
      }
      if (!rechartProps) {
        setTooltipProps(null);
      }

      const container = containerRef.current;
      if (!container) {
        return;
      }

      // Set the fixed position of the tooltip next to the dot
      const tooltipX =
        container.getBoundingClientRect().x +
        rechartProps.x +
        TOOLTIP_POSITION_OFFSET;
      const tooltipY = container.getBoundingClientRect().y + rechartProps.y;

      setTooltipProps({
        line,
        xAxisKey: rechartProps.xAxisKey,
        x: tooltipX,
        y: tooltipY,
        date: rechartProps.date,
        timeUnit,
        startDate,
        endDate,
        highlight,
        xAxisLabelIndex: rechartProps.xAxisLabelIndex,
      });
    },
    [isTooltipLocked, containerRef, endDate, startDate, timeUnit],
  );

  const onMouseLeave = useCallback(() => {
    if (isTooltipLocked) {
      return;
    }
    // Set tooltip to null after 50ms if no mouse movement
    debouncedSetTooltipProps(null);
  }, [isTooltipLocked, debouncedSetTooltipProps]);

  const lockTooltipPositionHandler = useCallback(() => {
    if (!tooltipProps) {
      return;
    }
    setIsTooltipLocked(true);
  }, [tooltipProps]);

  const clearLockedTooltipProps = useCallback(() => {
    if (isTooltipLocked) {
      setIsTooltipLocked(false);
      setTooltipProps(null);
    }
  }, [isTooltipLocked]);

  useTooltipScrollCancellation({
    closeTooltip: () => {
      setIsTooltipLocked(false);
      setTooltipProps(null);
    },
  });

  return {
    tooltipProps,
    isTooltipLocked,
    onBarMouseMove,
    onLineDotMouseEnter,
    onMouseLeave,
    lockTooltipPositionHandler,
    clearLockedTooltipProps,
  };
};

export default useSingleChartDataTooltip;
